import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  Banner,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  Text,
  InlineStack,
  useIndexResourceState,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';

interface RewardTranslation {
  name?: string;
  description?: string;
}

interface Reward {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  pointsCost: number;
  rewardType: string;
  stock: number | null;
  active: boolean;
  sortOrder: number;
  translations: Record<string, RewardTranslation>;
  createdAt: string;
}

const TRANSLATION_LANGS = [
  { code: 'en', label: '🇬🇧 English' },
  { code: 'fr', label: '🇫🇷 Français' },
  { code: 'de', label: '🇩🇪 Deutsch' },
  { code: 'it', label: '🇮🇹 Italiano' },
  { code: 'pt', label: '🇵🇹 Português' },
  { code: 'nl', label: '🇳🇱 Nederlands' },
  { code: 'pl', label: '🇵🇱 Polski' },
] as const;

interface Redemption {
  id: string;
  customerId: string;
  rewardId: string;
  pointsSpent: number;
  status: string;
  createdAt: string;
  customerEmail: string;
  rewardName: string;
}

export default function Rewards() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pointsCost, setPointsCost] = useState('100');
  const [rewardType, setRewardType] = useState('product');
  const [stock, setStock] = useState('');
  const [shopifyVariantId, setShopifyVariantId] = useState('');
  const [shopifyProductId, setShopifyProductId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [translations, setTranslations] = useState<Record<string, RewardTranslation>>({});
  const [showTranslations, setShowTranslations] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<Array<{
    id: string; title: string; imageUrl: string | null;
    variants: Array<{ id: string; title: string; price: string }>;
  }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rewardsData, redemptionsData] = await Promise.all([
        apiGet<{ rewards: Reward[] }>('/panel-api/rewards'),
        apiGet<{ redemptions: Redemption[] }>('/panel-api/rewards/redemptions'),
      ]);
      setRewards(rewardsData.rewards);
      setRedemptions(redemptionsData.redemptions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchProducts = async (q: string) => {
    if (!q || q.length < 2) { setProductResults([]); return; }
    setSearchLoading(true);
    try {
      const data = await apiGet<{ products: typeof productResults }>(`/panel-api/products/search?q=${encodeURIComponent(q)}`);
      setProductResults(data.products);
    } catch { setProductResults([]); }
    finally { setSearchLoading(false); }
  };

  const selectProduct = (product: typeof productResults[0], variantIdx = 0) => {
    const variant = product.variants[variantIdx];
    setName(product.title + (variant.title !== 'Default Title' ? ` - ${variant.title}` : ''));
    setShopifyProductId(product.id);
    setShopifyVariantId(variant.id);
    setImageUrl(product.imageUrl ?? '');
    setProductResults([]);
    setProductSearch('');
  };

  const openCreate = () => {
    setEditingReward(null);
    setName('');
    setDescription('');
    setPointsCost('100');
    setRewardType('product');
    setStock('');
    setShopifyVariantId('');
    setShopifyProductId('');
    setImageUrl('');
    setTranslations({});
    setShowTranslations(false);
    setProductSearch('');
    setProductResults([]);
    setShowModal(true);
  };

  const openEdit = (reward: Reward) => {
    setEditingReward(reward);
    setName(reward.name);
    setDescription(reward.description ?? '');
    setPointsCost(String(reward.pointsCost));
    setRewardType(reward.rewardType);
    setStock(reward.stock !== null ? String(reward.stock) : '');
    setShopifyVariantId((reward as any).shopifyVariantId ?? '');
    setShopifyProductId((reward as any).shopifyProductId ?? '');
    setImageUrl(reward.imageUrl ?? '');
    setTranslations(reward.translations ?? {});
    setShowTranslations(Object.keys(reward.translations ?? {}).length > 0);
    setShowModal(true);
  };

  const updateTranslation = (lang: string, field: 'name' | 'description', value: string) => {
    setTranslations((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value || undefined },
    }));
  };

  const handleSave = async () => {
    // Clean empty translations
    const cleanTranslations: Record<string, RewardTranslation> = {};
    for (const [lang, t] of Object.entries(translations)) {
      if (t.name || t.description) {
        cleanTranslations[lang] = t;
      }
    }

    const data = {
      name,
      description: description || undefined,
      imageUrl: imageUrl || undefined,
      pointsCost: parseInt(pointsCost),
      rewardType,
      shopifyVariantId: shopifyVariantId || undefined,
      shopifyProductId: shopifyProductId || undefined,
      stock: stock ? parseInt(stock) : undefined,
      translations: cleanTranslations,
    };

    try {
      if (editingReward) {
        await apiPut(`/panel-api/rewards/${editingReward.id}`, data);
      } else {
        await apiPost('/panel-api/rewards', data);
      }
      setShowModal(false);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deactivate this reward?')) return;
    try {
      await apiDelete(`/panel-api/rewards/${id}`);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(rewards as any);

  const rowMarkup = rewards.map((reward, idx) => (
    <IndexTable.Row
      id={reward.id}
      key={reward.id}
      position={idx}
      selected={selectedResources.includes(reward.id)}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="bold">
          {reward.name}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{reward.pointsCost.toLocaleString()} pts</IndexTable.Cell>
      <IndexTable.Cell>{reward.rewardType}</IndexTable.Cell>
      <IndexTable.Cell>{reward.stock ?? '∞'}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={reward.active ? 'success' : undefined}>
          {reward.active ? 'Active' : 'Inactive'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => openEdit(reward)}>Edit</Button>
          {reward.active && (
            <Button size="slim" tone="critical" onClick={() => handleDelete(reward.id)}>
              Deactivate
            </Button>
          )}
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Rewards"
      primaryAction={{ content: 'Add reward', onAction: openCreate }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: 'reward', plural: 'rewards' }}
              itemCount={rewards.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: 'Name' },
                { title: 'Cost' },
                { title: 'Type' },
                { title: 'Stock' },
                { title: 'Status' },
                { title: 'Actions' },
              ]}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Recent Redemptions</Text>
              {redemptions.length === 0 ? (
                <Text as="p" tone="subdued">No redemptions yet</Text>
              ) : (
                <IndexTable
                  resourceName={{ singular: 'redemption', plural: 'redemptions' }}
                  itemCount={redemptions.length}
                  headings={[
                    { title: 'Customer' },
                    { title: 'Reward' },
                    { title: 'Points' },
                    { title: 'Status' },
                    { title: 'Date' },
                  ]}
                  selectable={false}
                >
                  {redemptions.map((r, idx) => (
                    <IndexTable.Row id={r.id} key={r.id} position={idx}>
                      <IndexTable.Cell>{r.customerEmail}</IndexTable.Cell>
                      <IndexTable.Cell>{r.rewardName}</IndexTable.Cell>
                      <IndexTable.Cell>{r.pointsSpent}</IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={r.status === 'fulfilled' ? 'success' : r.status === 'pending' ? 'attention' : undefined}>
                          {r.status}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{new Date(r.createdAt).toLocaleDateString()}</IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingReward ? 'Edit Reward' : 'Add Reward'}
        primaryAction={{
          content: 'Save',
          onAction: handleSave,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="h3" variant="headingSm">Producto de Shopify</Text>
            <TextField
              label="Buscar producto"
              value={productSearch}
              onChange={(v) => { setProductSearch(v); searchProducts(v); }}
              autoComplete="off"
              placeholder="Escribe para buscar..."
              loading={searchLoading}
            />
            {productResults.length > 0 && (
              <Card>
                <BlockStack gap="200">
                  {productResults.map((p) => (
                    <div key={p.id}>
                      {p.variants.map((v, vi) => (
                        <Button
                          key={v.id}
                          fullWidth
                          textAlign="start"
                          variant="plain"
                          onClick={() => selectProduct(p, vi)}
                        >
                          {p.title}{v.title !== 'Default Title' ? ` – ${v.title}` : ''} ({v.price}€)
                        </Button>
                      ))}
                    </div>
                  ))}
                </BlockStack>
              </Card>
            )}
            {shopifyVariantId && (
              <Banner tone="success">
                <p>Producto vinculado: <strong>{name}</strong></p>
              </Banner>
            )}
            <TextField
              label="Nombre del regalo"
              value={name}
              onChange={setName}
              autoComplete="off"
            />
            <TextField
              label="Descripción (Español — idioma base)"
              value={description}
              onChange={setDescription}
              multiline={3}
              autoComplete="off"
            />
            <InlineStack align="start">
              <Button
                variant="plain"
                onClick={() => setShowTranslations(!showTranslations)}
                icon={showTranslations ? undefined : undefined}
              >
                {showTranslations ? '▾ Ocultar traducciones' : '▸ Traducciones a otros idiomas'}
              </Button>
            </InlineStack>
            {showTranslations && (
              <BlockStack gap="400">
                {TRANSLATION_LANGS.map(({ code, label }) => (
                  <Card key={code}>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingSm">{label}</Text>
                      <TextField
                        label="Nombre"
                        value={translations[code]?.name ?? ''}
                        onChange={(v) => updateTranslation(code, 'name', v)}
                        autoComplete="off"
                        placeholder={name || 'Nombre en este idioma'}
                      />
                      <TextField
                        label="Descripción"
                        value={translations[code]?.description ?? ''}
                        onChange={(v) => updateTranslation(code, 'description', v)}
                        multiline={2}
                        autoComplete="off"
                        placeholder={description || 'Descripción en este idioma'}
                      />
                    </BlockStack>
                  </Card>
                ))}
              </BlockStack>
            )}
            <TextField
              label="Coste en puntos"
              type="number"
              value={pointsCost}
              onChange={setPointsCost}
              autoComplete="off"
            />
            <Select
              label="Tipo de regalo"
              options={[
                { label: 'Producto', value: 'product' },
                { label: 'Descuento', value: 'discount' },
                { label: 'Experiencia', value: 'experience' },
              ]}
              value={rewardType}
              onChange={setRewardType}
            />
            <TextField
              label="Stock (vacío = ilimitado)"
              type="number"
              value={stock}
              onChange={setStock}
              autoComplete="off"
            />
            <TextField
              label="URL de imagen"
              value={imageUrl}
              onChange={setImageUrl}
              autoComplete="off"
              helpText="Se rellena automáticamente al seleccionar un producto"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
